import React from 'react';

/**
 * Wiederverwendbare PageHero Komponente für einheitliche Unterseiten-Header
 * Mobile: Dortmund-Stil (Bild oben, Titel darunter)
 * Desktop: Moderner Stil (Gradient-Overlay über Bild)
 * 
 * Props:
 * - title: Hauptüberschrift (required)
 * - subtitle: Optionaler Untertitel
 * - badges: Array von Badge-Objekten [{icon: Component, text: string, variant: 'primary'|'white'}]
 * - teaserTitle: Titel für die Teaser-Box (optional)
 * - teaserText: Text für die Teaser-Box (optional)
 * - backgroundImage: URL für das Hintergrundbild (required)
 * - rightContent: Optionaler React-Knoten für die rechte Seite (Buttons, Info-Boxen, etc.)
 * - variant: 'full' (mit Teaser-Box) | 'simple' (nur Titel + Subtitle, für Legal-Seiten)
 */
const PageHero = ({
  title,
  subtitle,
  badges = [],
  teaserTitle,
  teaserText,
  backgroundImage,
  rightContent,
  variant = 'full'
}) => {
  return (
    <>
      {/* MOBILE: Klassischer Hero wie Dortmund (Bild oben, Titel darunter) */}
      <div className="md:hidden">
        {/* Vollbreites Bild */}
        <div className="w-full">
          <img 
            src={backgroundImage}
            alt={title}
            className="w-full h-auto object-cover"
            style={{ aspectRatio: '16/9' }}
          />
        </div>
        
        {/* Titel-Bereich unter dem Bild */}
        <div className="bg-white px-4 py-6">
          <h1 className="text-2xl font-bold text-[#1e3a5f] leading-tight mb-3" data-testid="page-hero-title-mobile">
            {title}
          </h1>
          {subtitle && (
            <p className="text-slate-600 text-base leading-relaxed">
              {subtitle}
            </p>
          )}
        </div>
      </div>

      {/* DESKTOP: Bestehender Hero mit Gradient-Overlay (unverändert) */}
      <div className="hidden md:block">
        <div className="max-w-[1664px] mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div 
            className="relative overflow-hidden rounded-xl"
            style={{ 
              backgroundImage: `url("${backgroundImage}")`,
              backgroundSize: '100% 100%',
              backgroundPosition: 'center center'
            }}
          >
            {/* Gradient Overlay - Links für Textbereich */}
            <div className="absolute inset-0 bg-gradient-to-r from-[#1e3a5f]/90 via-[#1e3a5f]/70 to-[#1e3a5f]/20" />
            
            {/* Content - Einheitliche Höhe */}
            <div className="relative px-8 lg:px-10 py-12 lg:py-14">
              <div className="flex flex-row items-center justify-between gap-6 min-h-[240px]">
                {/* Titel + Zusatzinfo - Links */}
                <div className="max-w-xl">
                  {/* Badges */}
                  {badges.length > 0 && (
                    <div className="flex flex-wrap items-center gap-2 mb-4">
                      {badges.map((badge, index) => {
                        const IconComponent = badge.icon;
                        const isWhite = badge.variant === 'white';
                        return (
                          <span 
                            key={index}
                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold shadow-sm ${
                              isWhite 
                                ? 'bg-white/95 text-[#1e3a5f]' 
                                : badge.bgColor || 'bg-emerald-500 text-white'
                            }`}
                          >
                            {IconComponent && <IconComponent className="w-3.5 h-3.5" />}
                            {badge.text}
                          </span>
                        );
                      })}
                    </div>
                  )}
                  
                  {/* Titel */}
                  <h1 className="text-3xl lg:text-4xl font-bold text-white leading-tight drop-shadow-sm" data-testid="page-hero-title">
                    {title}
                  </h1>
                  
                  {/* Subtitle */}
                  {subtitle && (
                    <p className="text-white/90 text-lg mt-3 drop-shadow-sm">
                      {subtitle}
                    </p>
                  )}
                  
                  {/* Teaser-Box (nur bei variant='full') */}
                  {variant === 'full' && teaserTitle && teaserText && (
                    <div className="mt-6 p-4 bg-white/10 backdrop-blur-sm rounded-lg border border-white/20">
                      <p className="text-white font-semibold text-lg">
                        {teaserTitle}
                      </p>
                      <p className="text-white/80 text-sm mt-1">
                        {teaserText}
                      </p>
                    </div>
                  )}
                </div>
                
                {/* Rechte Seite - Optionaler Content */}
                {rightContent && (
                  <div className="flex-shrink-0 flex items-center">
                    {rightContent}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export { PageHero };
